import type { AssetFilter } from "../api/assets";
import type { AttributeView } from "../api/catalog";

/** How a field is edited and matched (mirrors `AssetSearchService.Kind`). */
export type SearchFieldKind = "values" | "text" | "number" | "date" | "tree";

export type SearchFieldGroup = "basic" | "place" | "replacement" | "attribute";

export type SearchField = {
  /** What the API calls it: a column name or `attr:<key>`. */
  name: string;
  label: string;
  kind: SearchFieldKind;
  group: SearchFieldGroup;
  unit?: string | null;
  /** Whether "nije popunjeno" makes sense (the field may be blank on an asset). */
  nullable: boolean;
  /** The complete list of values, for fields that have one (so the choices do not come and go). */
  fixedValues?: string[];
  /** The attribute behind an `attr:` field. */
  attribute?: AttributeView;
  /** Function the picker of this field needs (the tree it draws on). */
  permission?: string;
};

/**
 * A criterion as the screen holds it: the API part plus the names of picked
 * rows (a position, a type…), kept so a pasted link shows them without a lookup.
 */
export type FilterState = AssetFilter & { labels?: Record<string, string> };

type Translate = (key: string, fallback: string) => string;

/** The columns and trees the search knows besides attributes, in the order the picker lists them. */
export function fixedSearchFields(t: Translate): SearchField[] {
  return [
    { name: "typeId", label: t("advSearch.field.type", "Tip dela"), kind: "values", group: "basic", nullable: false },
    { name: "condition", label: t("assets.field.condition", "Stanje"), kind: "values", group: "basic", nullable: false, fixedValues: ["ISPRAVAN", "NEISPRAVAN", "NA_SERVISU", "OTPISAN"] },
    { name: "usage", label: t("assets.field.usage", "Upotreba"), kind: "values", group: "basic", nullable: false, fixedValues: ["MONTIRAN", "NA_LAGERU"] },
    { name: "manufacturerId", label: t("advSearch.field.manufacturer", "Proizvođač"), kind: "values", group: "basic", nullable: true },
    { name: "supplierId", label: t("advSearch.field.supplier", "Dobavljač"), kind: "values", group: "basic", nullable: true },
    { name: "customMade", label: t("advSearch.field.customMade", "Sopstvena izrada"), kind: "values", group: "basic", nullable: false, fixedValues: ["true", "false"] },
    { name: "tag", label: t("assets.col.tag", "Oznaka"), kind: "text", group: "basic", nullable: false },
    { name: "inventoryNo", label: t("advSearch.field.inventoryNo", "ID asseta"), kind: "text", group: "basic", nullable: false },
    { name: "serialNo", label: t("assets.field.serialNo", "Serijski broj"), kind: "text", group: "basic", nullable: true },
    { name: "manufacturerCode", label: t("advSearch.field.manufacturerCode", "Oznaka proizvođača"), kind: "text", group: "basic", nullable: true },
    { name: "positionId", label: t("assets.col.position", "Logička pozicija"), kind: "tree", group: "place", nullable: false, permission: "structure:view" },
    { name: "locationId", label: t("assets.col.location", "Fizička lokacija"), kind: "tree", group: "place", nullable: false, permission: "locations:view" },
    { name: "replacementKind", label: t("advSearch.field.replacementKind", "Vrsta zamene"), kind: "values", group: "replacement", nullable: false, fixedValues: ["PERMANENT", "TEMPORARY"] },
    { name: "replacementReminderAt", label: t("advSearch.field.reminder", "Podsetnik zamene"), kind: "date", group: "replacement", nullable: true },
    { name: "expectedReturnAt", label: t("advSearch.field.expectedReturn", "Očekivani povratak"), kind: "date", group: "replacement", nullable: true },
  ];
}

export const ATTRIBUTE_PREFIX = "attr:";

/** The search field of a codebook attribute; the data type decides how it is matched. */
export function attributeSearchField(a: AttributeView): SearchField {
  const base = { name: `${ATTRIBUTE_PREFIX}${a.key}`, label: a.label, unit: a.unit, group: "attribute" as const, nullable: true, attribute: a };
  switch (a.dataType) {
    case "NUMBER": return { ...base, kind: "number" };
    // A span (RANGE): the same od–do row; the server matches spans that overlap it (sprint 5i §2).
    case "RANGE": return { ...base, kind: "number" };
    case "DATE": return { ...base, kind: "date" };
    case "BOOLEAN": return { ...base, kind: "values", fixedValues: ["true", "false"] };
    case "ENUM": return { ...base, kind: "values", fixedValues: (a.enumOptions ?? []).map((o) => o.value) };
    default: return { ...base, kind: "values" };
  }
}

/** "Snaga (kW)". */
export function fieldTitle(f: Pick<SearchField, "label" | "unit">): string {
  return f.unit ? `${f.label} (${f.unit})` : f.label;
}

/** Whether the criterion restricts anything yet (a freshly added row does not). */
export function isActiveFilter(f: AssetFilter): boolean {
  return !!f.empty
    || (f.values?.length ?? 0) > 0
    || !!f.text?.trim()
    || !!f.from?.trim()
    || !!f.to?.trim();
}

/** A number the way it is typed here: digits with an optional decimal comma or point. */
const TYPED_NUMBER = /^-?(\d+[.,]?\d*|[.,]\d+)$/;

/**
 * What is wrong with a range as it stands — `"number"` (an end is not a number) or `"order"`
 * (the upper end is below the lower) — or null. Half-typed input lands here all the time
 * ("150" starts as "1"), so the screen holds such a criterion back instead of sending it.
 */
export function rangeProblem(kind: SearchFieldKind, f: AssetFilter): "number" | "order" | null {
  if (f.empty || (kind !== "number" && kind !== "date")) return null;
  const from = f.from?.trim() ?? "";
  const to = f.to?.trim() ?? "";
  if (kind === "date") return from && to && from > to ? "order" : null;
  if ((from && !TYPED_NUMBER.test(from)) || (to && !TYPED_NUMBER.test(to))) return "number";
  if (from && to && Number(from.replace(",", ".")) > Number(to.replace(",", "."))) return "order";
  return null;
}

/** What goes to the API: only criteria that restrict something, without the screen-only labels. */
export function toRequestFilters(filters: FilterState[]): AssetFilter[] {
  const out: AssetFilter[] = [];
  for (const f of filters) {
    if (!isActiveFilter(f)) continue;
    const clean: AssetFilter = { field: f.field };
    if (f.values?.length) clean.values = f.values;
    if (f.text?.trim()) clean.text = f.text.trim();
    if (f.from?.trim()) clean.from = f.from.trim();
    if (f.to?.trim()) clean.to = f.to.trim();
    if (f.empty) clean.empty = true;
    out.push(clean);
  }
  return out;
}

/** The `f` query parameter: the rows as JSON, so a search can be linked and survives "back". */
export function encodeFilters(filters: FilterState[]): string {
  return filters.length === 0 ? "" : JSON.stringify(filters);
}

/** Reads `f` back; anything that is not a list of criteria (a hand-edited link) yields no filters. */
export function decodeFilters(raw: string | null): FilterState[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: FilterState[] = [];
  const seen = new Set<string>();
  for (const item of parsed) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    if (typeof o.field !== "string" || seen.has(o.field)) continue;
    seen.add(o.field);
    const f: FilterState = { field: o.field };
    if (Array.isArray(o.values)) f.values = o.values.filter((v): v is string => typeof v === "string");
    if (typeof o.text === "string") f.text = o.text;
    if (typeof o.from === "string") f.from = o.from;
    if (typeof o.to === "string") f.to = o.to;
    if (o.empty === true) f.empty = true;
    if (o.labels && typeof o.labels === "object") {
      const labels: Record<string, string> = {};
      for (const [k, v] of Object.entries(o.labels as Record<string, unknown>)) {
        if (typeof v === "string") labels[k] = v;
      }
      f.labels = labels;
    }
    out.push(f);
  }
  return out;
}

/** `7.5` → `7,5`: range hints read the way numbers are typed here. */
export function formatNumberSr(value: string): string {
  return value.replace(".", ",");
}
